/// <reference path="../../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Storage pane — view + manage the on-device media cache (avatars, images and
// other media kept locally so they render instantly + offline). Platform-
// agnostic: the host computes the usage + supplies the clear action.

import { useState } from "react";
import { Text, View } from "react-native";
import { PillButton } from "../controls";
import { SettingsDivider, SettingsRow } from "../SettingsRow";

export interface StorageBucket {
	readonly key: string;
	readonly label: string;
	readonly description?: string;
	readonly bytes: number;
}

export interface StorageUsageData {
	/** Total bytes the media cache currently occupies. */
	readonly totalBytes: number;
	/** Origin storage quota (from `navigator.storage.estimate`), when known. */
	readonly quotaBytes?: number;
	/** Per-bucket breakdown for the list. */
	readonly buckets: readonly StorageBucket[];
}

export interface StoragePaneProps {
	/** `undefined`/`null` while the host computes usage. */
	readonly usage?: StorageUsageData | null;
	readonly onRefresh?: () => void;
	readonly onClearMediaCache?: () => Promise<void> | void;
	readonly clearing?: boolean;
}

function formatBytes(bytes: number): string {
	if (bytes <= 0) return "0 KB";
	const units = ["B", "KB", "MB", "GB", "TB"];
	let value = bytes;
	let i = 0;
	while (value >= 1024 && i < units.length - 1) {
		value /= 1024;
		i += 1;
	}
	const rounded = value >= 100 || i === 0 ? Math.round(value) : Math.round(value * 10) / 10;
	return `${rounded} ${units[i]}`;
}

export function StoragePane(props: StoragePaneProps): JSX.Element {
	const { usage, onRefresh, onClearMediaCache, clearing } = props;
	const [confirming, setConfirming] = useState(false);

	const loading = usage === undefined || usage === null;
	const total = usage?.totalBytes ?? 0;
	const quota = usage?.quotaBytes;
	const pct =
		quota !== undefined && quota > 0
			? Math.min(100, Math.max(total > 0 ? 1 : 0, (total / quota) * 100))
			: undefined;
	const buckets = (usage?.buckets ?? []).filter((b) => b.bytes > 0);

	return (
		<View className="flex flex-col gap-4">
			<SettingsRow
				variant="hero"
				title="Storage"
				description="Ping keeps images, avatars and media on this device so they appear instantly and work offline. Clearing the cache is safe — anything still in your chats re-downloads when you open it."
				testID="settings-storage-intro"
			/>

			{/* Usage summary card */}
			<View
				testID="settings-storage-usage"
				className="rounded-2xl border border-light-border-secondary dark:border-[#22242A] bg-light-surface-secondary dark:bg-[#15171C] p-4"
			>
				<Text className="text-[13px] leading-[18px] text-light-text-secondary dark:text-[#B5BAC3]">
					Media cache
				</Text>
				<Text className="mt-1 text-[28px] leading-[34px] font-bold text-light-text-primary dark:text-[#F5F6F8]">
					{loading ? "…" : formatBytes(total)}
				</Text>
				{pct !== undefined ? (
					<>
						<View className="mt-3 h-2 w-full overflow-hidden rounded-full bg-light-surface-tertiary dark:bg-[#22242A]">
							<View
								className="h-full rounded-full bg-light-accent-sky dark:bg-[#537EFF]"
								style={{ width: `${pct}%` }}
							/>
						</View>
						<Text className="mt-2 text-[11px] leading-[15px] text-light-text-tertiary dark:text-[#6B7280]">
							{formatBytes(total)} of {quota !== undefined ? formatBytes(quota) : "—"} used by
							this site
						</Text>
					</>
				) : null}
			</View>

			{/* Per-bucket breakdown */}
			{buckets.length > 0 ? (
				<View
					testID="settings-storage-breakdown"
					className="rounded-2xl border border-light-border-secondary dark:border-[#22242A] overflow-hidden"
				>
					{buckets.map((bucket, idx) => (
						<View key={bucket.key}>
							{idx > 0 ? <SettingsDivider /> : null}
							<View className="flex flex-row items-center justify-between px-4 py-3">
								<View className="flex-1 pr-3">
									<Text className="text-[14px] leading-[19px] font-medium text-light-text-primary dark:text-[#F5F6F8]">
										{bucket.label}
									</Text>
									{bucket.description !== undefined ? (
										<Text className="mt-0.5 text-[11px] leading-[15px] text-light-text-tertiary dark:text-[#6B7280]">
											{bucket.description}
										</Text>
									) : null}
								</View>
								<Text className="text-[13px] leading-[18px] tabular-nums text-light-text-secondary dark:text-[#B5BAC3]">
									{formatBytes(bucket.bytes)}
								</Text>
							</View>
						</View>
					))}
				</View>
			) : null}

			{/* Actions */}
			<SettingsRow
				title="Clear media cache"
				description="Free up space now. Images and media will re-download the next time you view them."
				testID="settings-storage-clear"
			>
				<View className="flex flex-row items-center gap-2">
					{onRefresh !== undefined ? (
						<PillButton variant="ghost" onClick={onRefresh} disabled={clearing === true}>
							Refresh
						</PillButton>
					) : null}
					{confirming ? (
						<>
							<PillButton
								variant="ghost"
								onClick={() => setConfirming(false)}
								disabled={clearing === true}
							>
								Cancel
							</PillButton>
							<PillButton
								variant="destructive"
								busy={clearing === true}
								onClick={() => {
									void Promise.resolve(onClearMediaCache?.()).finally(() =>
										setConfirming(false),
									);
								}}
							>
								Clear now
							</PillButton>
						</>
					) : (
						<PillButton
							variant="secondary"
							onClick={() => setConfirming(true)}
							disabled={loading || total <= 0}
						>
							Clear
						</PillButton>
					)}
				</View>
			</SettingsRow>
		</View>
	);
}
